import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";

// @mui material components
import {
  Card,
  CircularProgress,
  Fade,
  Grid,
  Icon,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Tooltip,
} from "@mui/material";

import { gql, useLazyQuery, useMutation } from "@apollo/client";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import SoftInputNumber from "components/SoftInputNumberUpgrade";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "50%", xxl: "40%" },
  overflowY: "auto",
  p: 3,
};
const arrayCategorias = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "1.A",
  "1.B",
  "1.C",
  "1.D",
  "1.E",
  "2.A",
  "2.B",
  "2.C",
  "2.D",
  "2.E",
];

export default function ModalTasas({ open, handleClose, refetch, idTasa }) {
  const [tasa, setTasa] = useState({
    categoria: "A",
  });
  const { handleSnackbar } = useContext(MessageManager);

  const handleChanges = (e) => {
    setTasa({
      ...tasa,
      [e.target.name]: e.target.value,
    });
  };
  const [getTasa, { data: dataTasa }] = useLazyQuery(
    gql`
      query getTasa($id: ID!) {
        tasa(id: $id) {
          id
          categoria
          importe
          porcentaje
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
    }
  );

  const [saveTasa, { loading: loadingSave }] = useMutation(
    gql`
      mutation saveTasa($input: TasaInput!) {
        saveTasa(input: $input) {
          id
        }
      }
    `
  );

  const handleSave = () => {
    saveTasa({
      variables: {
        input: {
          id: tasa?.id || null,
          categoria: tasa?.categoria,
          importe: tasa?.importe ? parseFloat(tasa?.importe) : null,
          porcentaje: tasa?.porcentaje ? parseInt(tasa?.porcentaje) : null,
        },
      },
    })
      .then(() => {
        refetch();
        handleSnackbar("Tasa guardada correctamente", "success");
        handleClose();
      })
      .catch((e) => {
        handleSnackbar(e.message || "Error al guardar la tasa", "error");
      });
  };

  useEffect(() => {
    if (idTasa && open) {
      getTasa({ variables: { id: idTasa } });
    }
  }, [idTasa, open]);

  useEffect(() => {
    if (dataTasa && idTasa && open) {
      setTasa(dataTasa.tasa);
    } else {
      setTasa({
        categoria: "A",
      });
    }
  }, [dataTasa, idTasa, open]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center">
              <SoftTypography variant="h6">
                {tasa?.id ? "Editar Tasa" : "Agregar Tasa"}
              </SoftTypography>
              <Tooltip title="Cerrar" placement="top">
                <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                  close
                </Icon>
              </Tooltip>
            </SoftBox>
            <SoftBox py={3}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <InputLabel htmlFor="categoria">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Categoría
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <Select
                    fullWidth
                    size="small"
                    labelId="categoria"
                    id="categoria"
                    name="categoria"
                    value={tasa?.categoria || "A"}
                    onChange={handleChanges}
                    inputProps={{ "aria-label": "Without label" }}
                    defaultValue="A"
                    disabled={tasa?.id}
                  >
                    {arrayCategorias.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                {tasa?.categoria?.length > 1 ? (
                  <Grid item xs={12}>
                    <InputLabel htmlFor="porcentaje">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Porcentaje
                        <SoftTypography variant="caption" fontWeight="bold" color="primary">
                          &nbsp;*
                        </SoftTypography>
                      </SoftTypography>
                    </InputLabel>
                    <SoftInputNumber
                      icon={{
                        component: "percent",
                        direction: "right",
                      }}
                      label="porcentaje"
                      placeholder="Ingrese el porcentaje"
                      type="number"
                      name="porcentaje"
                      value={tasa?.porcentaje || ""}
                      onChange={handleChanges}
                      inputProps={{ min: 1, step: 1 }}
                    />
                  </Grid>
                ) : (
                  <Grid item xs={12}>
                    <InputLabel htmlFor="importe">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Importe
                        <SoftTypography variant="caption" fontWeight="bold" color="primary">
                          &nbsp;*
                        </SoftTypography>
                      </SoftTypography>
                    </InputLabel>
                    <SoftInputNumber
                      icon={{
                        component: "attach_money",
                        direction: "left",
                      }}
                      label="importe"
                      placeholder="Ingrese el importe"
                      type="number"
                      name="importe"
                      value={tasa?.importe || ""}
                      onChange={handleChanges}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>
                )}
                {/* <Grid item xs={12}>
                  <InputLabel htmlFor="año">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Año
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="año"
                    placeholder="Ingrese el año"
                    type="number"
                    name="anio"
                    value={tasa?.anio || ""}
                    onChange={handleChanges}
                    inputProps={{ min: 2024, step: 1 }}
                  />
                </Grid> */}
              </Grid>
            </SoftBox>
            <SoftBox display="flex" justifyContent="flex-end" alignItems="center">
              <SoftButton color="primary" onClick={handleClose} sx={{ marginRight: 1 }}>
                <Icon sx={{ fontWeight: "light" }}>cancel</Icon>
                &nbsp;Cancelar
              </SoftButton>
              <SoftButton
                color="gobSecondary"
                disabled={loadingSave || (!tasa?.importe && !tasa?.porcentaje)}
                onClick={handleSave}
              >
                {loadingSave ? (
                  <CircularProgress size={15} sx={{ marginRight: 0.5 }} color="inherit" />
                ) : (
                  <Icon sx={{ fontWeight: "light" }}>save</Icon>
                )}
                &nbsp;Guardar
              </SoftButton>
            </SoftBox>
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalTasas.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  refetch: PropTypes.func,
  idTasa: PropTypes.string,
};
